import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { FileTextIcon } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FilePreviewDialog } from "@/components/file-preview/FilePreviewDialog";
import { buildFileUrl } from "@/lib/utils/buildFileUrl";

interface FilePreviewLinkProps {
  s3Key?: string;
  displayFileName?: string;
  variant?: "default" | "icon";
  className?: string;
}

export const FilePreviewLink = ({
  s3Key,
  displayFileName,
  variant = "default",
  className,
}: FilePreviewLinkProps) => {
  const { t } = useTranslation();
  const [previewDialog, setPreviewDialog] = useState<{
    fileUrl: string;
    fileName: string;
  } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const onClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      const fileUrl = buildFileUrl(s3Key);
      if (fileUrl) {
        setPreviewDialog({
          fileUrl,
          fileName: displayFileName || "",
        });
      }
    },
    [s3Key, displayFileName],
  );

  const onMouseDown = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
  }, []);

  const handleClosePreview = useCallback(() => {
    setPreviewDialog(null);
  }, []);

  if (!s3Key || !displayFileName) {
    return (
      <span className="text-gray-400 italic">
        {t("contractorCards.messages.noCard")}
      </span>
    );
  }

  if (variant === "icon") {
    return (
      <Button
        ref={buttonRef}
        variant="ghost"
        size="icon"
        onClick={onClick}
        onMouseDown={onMouseDown}
        className={cn(
          "h-5 w-5 p-0 inline-flex items-center justify-center text-gray-500 hover:text-blue-600",
          className,
        )}
        title={t("common.preview")}
      >
        <FileTextIcon className="h-3 w-3" />
      </Button>
    );
  }

  return (
    <>
      <Button
        ref={buttonRef}
        variant="ghost"
        size="sm"
        onClick={onClick}
        onMouseDown={onMouseDown}
        className={cn("h-auto p-1 flex items-center justify-start", className)}
      >
        <FileTextIcon className="w-4 h-4 mr-2 text-blue-500" />
        <span className="text-blue-600 hover:text-blue-800 underline">
          {displayFileName}
        </span>
      </Button>
      <FilePreviewDialog
        open={Boolean(previewDialog)}
        onClose={handleClosePreview}
        fileUrl={previewDialog?.fileUrl || ""}
        fileName={previewDialog?.fileName}
      />
    </>
  );
};
